<template>
  <div class="lotteries-container">
    <!-- Cabeçalho da página -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-dice"></i>
        Loterias
      </h1>
      <p class="page-subtitle">Gerencie as loterias e seus horários</p>
    </div>

    <!-- Navegação por tabs -->
    <div class="tabs-container">
      <nav class="modern-tabs">
        <button 
          class="tab-button" 
          :class="{ active: selectedTab === 1 }" 
          @click="selectTab(1)"
        > 
          <i class="fa-solid fa-list"></i>
          LISTAGEM
        </button>
        <button 
          class="tab-button" 
          :class="{ 
            active: selectedTab === 2,
            disabled: !canCreateEditLotteries && !canEditSchedules 
          }" 
          @click="selectTab(2)"
          :disabled="!canCreateEditLotteries && !canEditSchedules"
          :title="(!canCreateEditLotteries && !canEditSchedules) ? 'Você não tem permissão para criar/editar loterias' : ''"
        >
          <i class="fa-solid fa-plus tab-plus-icon"></i>
          {{ lottery.id ? 'EDITAR' : 'CADASTRO' }}
        </button>
      </nav>
    </div>

    <!-- Loading -->
    <div v-show="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando...</span>
      </div>
    </div>

    <!-- Tab 1: Listagem -->
    <div v-show="selectedTab === 1 && !loading" class="tab-content">
      <div class="table-card">
        <!-- Cabeçalho da tabela -->
        <div class="table-header">
          <div class="search-container">
            <div class="search-input-group">
              <i class="fa-solid fa-search search-icon"></i>
              <input 
                type="text" 
                v-model="quickFilter" 
                class="search-input" 
                placeholder="Pesquisar loterias..." 
                autocomplete="off"
              />
            </div>
          </div>
          <button 
            type="button" 
            class="btn-primary" 
            @click="createLottery()" 
            :disabled="!canCreateEditLotteries"
            :title="!canCreateEditLotteries ? 'Você não tem permissão para criar loterias' : 'Adicionar nova loteria'"
          >
            <i class="fa-solid fa-plus"></i>
            Adicionar Loteria
          </button>
        </div>

        <!-- Mensagem informativa para usuários com permissões limitadas -->
        <div v-if="!isAdmin" class="permission-info">
          <div class="info-card">
            <i class="fa-solid fa-info-circle"></i>
            <div class="info-content">
              <span v-if="isBanca">
                <strong>Modo BANCA:</strong> Você pode visualizar apenas as loterias configuradas em sua região e editar apenas os horários dos sorteios.
              </span>
              <span v-else-if="isGerente">
                <strong>Modo GERENTE:</strong> Você pode visualizar apenas as loterias configuradas em sua região. Criação e edição não são permitidas.
              </span>
            </div>
          </div>
        </div>

        <!-- DataTable Cards Compactos -->
        <div class="lottery-datatable">
          <div 
            v-for="lotteryGroup in filteredLotteryGroups" 
            :key="lotteryGroup.lotteryId"
            class="lottery-group-row"
          >
            <!-- Header com Nome da Loteria -->
            <div 
              class="lottery-group-header" 
              :class="{ 'expanded': isLotteryExpanded(lotteryGroup.lotteryId) }"
              @click="toggleLottery(lotteryGroup.lotteryId)"
            >
              <div class="lottery-main-info">
                <i 
                  class="fa-solid fa-chevron-down accordion-icon"
                  :class="{ 'rotated': isLotteryExpanded(lotteryGroup.lotteryId) }"
                ></i>
                <h4 class="lottery-main-title">
                  <i class="fa-solid fa-dice"></i>
                  {{ lotteryGroup.lotteryName }}
                </h4>
              </div>
              <div class="lottery-main-actions" @click.stop>
                <button 
                  class="btn-action btn-edit" 
                  @click="editLottery(lotteryGroup.lotteryId)"
                  title="Editar"
                  :disabled="!canEditSchedules"
                >
                  <i class="fa-solid fa-pen-to-square"></i>
                </button>
                <button 
                  v-if="canDeleteLotteries"
                  class="btn-action btn-delete" 
                  @click="deleteLottery(lotteryGroup.lotteryId)"
                  title="Excluir"
                  :disabled="!canDeleteLotteries"
                >
                  <i class="fa-solid fa-trash"></i>
                </button>
              </div>
            </div>

            <!-- Cards dos Horários lado a lado -->
            <transition name="accordion">
              <div 
                v-show="isLotteryExpanded(lotteryGroup.lotteryId)"
                class="schedule-cards-container"
              >
              <div 
                v-for="schedule in lotteryGroup.schedules" 
                :key="schedule.lotteryDetailId"
                class="schedule-card"
              >
                <!-- Horário -->
                <div class="schedule-card-header">
                  <div class="schedule-time">
                    <i class="fa-solid fa-clock"></i>
                    <span class="time-text">{{ schedule.lotteryDetailDescription }}</span>
                  </div>
                  <span 
                    :class="['status-dot', schedule.isActive ? 'status-active' : 'status-inactive']"
                    :title="schedule.isActive ? 'Ativo' : 'Inativo'"
                  >
                  </span>
                </div>

                <!-- Dias da Semana -->
                <div class="schedule-card-days">
                  <span class="days-mini-label">Dias:</span>
                  <div class="days-badges-mini">
                    <span 
                      v-for="day in getActiveDays(schedule)" 
                      :key="day"
                      class="day-badge-mini active"
                    >
                      {{ day }}
                    </span>
                    <span v-if="getActiveDays(schedule).length === 0" class="no-days-mini">
                      Nenhum
                    </span>
                  </div>
                </div>
              </div>

              <!-- Estado quando não há horários configurados -->
              <div v-if="lotteryGroup.schedules.length === 0" class="no-schedules-card">
                <i class="fa-solid fa-exclamation-triangle"></i>
                <span>Nenhum horário configurado</span>
              </div>
              </div>
            </transition>
          </div>

          <!-- Estado vazio -->
          <div v-if="filteredLotteryGroups.length === 0" class="empty-state">
            <div class="empty-state-content">
              <i class="fa-solid fa-inbox"></i>
              <h3>Nenhuma loteria encontrada</h3>
              <p v-if="isAdmin && !currentRegion">
                Como ADMIN, selecione uma região para ver as configurações específicas ou visualize todas as loterias cadastradas.
              </p>
              <p v-else-if="!isAdmin && !currentRegion">
                Selecione uma região para visualizar as loterias disponíveis.
              </p>
              <p v-else>
                Não há loterias ativas cadastradas ou nenhuma corresponde aos filtros aplicados.
              </p>
              <small v-if="currentRegion" class="empty-state-note">
                <i class="fa-solid fa-info-circle"></i>
                Apenas loterias com pelo menos um horário ativo são exibidas
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tab 2: Formulário -->
    <div v-show="selectedTab === 2" class="tab-content">
      <div class="form-card">
        <div class="form-header">
          <h3 class="form-title">
            <i class="fa-solid fa-dice"></i>
            {{ lottery.id ? 'Editar Loteria' : 'Nova Loteria' }}
          </h3>
          <p class="form-subtitle">
            {{ lottery.id ? 'Atualize as informações da loteria' : 'Preencha as informações da nova loteria' }}
          </p>
        </div>

        <div class="form-body">
          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-tag"></i>
              Nome da Loteria
            </label>
            
            <!-- Mensagem informativa para BANCA -->
            <div v-if="isBanca" class="banca-info">
              <i class="fa-solid fa-info-circle"></i>
              <span>Como usuário BANCA, você pode editar apenas os horários dos sorteios, não o nome da loteria.</span>
            </div>
            
            <input 
              class="form-input" 
              type="text" 
              v-model="lottery.name"
              placeholder="Digite o nome da loteria..."
              :readonly="!isAdmin"
              :disabled="!isAdmin"
            />
          </div>

          <!-- Seção de Horários -->
          <div class="schedules-section">
            <div class="schedules-header">
              <h4 class="schedules-title">
                <i class="fa-solid fa-clock"></i>
                Horários dos Sorteios
              </h4>
              <p class="schedules-subtitle">
                Selecione os horários em que esta loteria será sorteada
              </p>
            </div>

            <div class="schedules-container">
              <div 
                v-for="(d, index) in lottery.details" 
                :key="index"
                v-show="d.operation != 'Delete'"
                class="schedule-item"
              >
                <div class="schedule-input-group" @click="focusTimeInput($event)">
                  <i class="fa-solid fa-clock schedule-icon"></i>
                  <input 
                    class="schedule-input" 
                    type="time" 
                    v-model="d.description"
                    placeholder="Selecione o horário..."
                    @click="openTimePicker($event)"
                    @focus="openTimePicker($event)"
                    ref="timeInput"
                  />
                  <button 
                    type="button" 
                    class="btn-remove" 
                    @click="removeLotteryTime(d)"
                    title="Remover horário"
                    :disabled="!canEditSchedules"
                  >
                    <i class="fa-solid fa-trash"></i>
                  </button>
                </div>
              </div>

              <div class="add-schedule">
                <button 
                  type="button" 
                  class="btn-add-schedule" 
                  @click="addLotteryTime()"
                  :disabled="!canEditSchedules"
                >
                  <i class="fa-solid fa-plus"></i>
                  Adicionar Horário
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="form-footer">
          <button 
            type="button" 
            class="btn-primary" 
            @click="lottery.id ? updateLottery() : insertLottery()"
            :disabled="!canEditSchedules || saving"
          >
            <i :class="saving ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-save'"></i>
            {{ saving ? 'Salvando...' : (lottery.id ? 'Atualizar' : 'Salvar') }}
          </button>
          <button 
            type="button" 
            class="btn-secondary" 
            @click="reset()"
            :disabled="saving"
          >
            <i class="fa-solid fa-times"></i>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import { ROLES } from "@/services/permissions";

export default {
  data: () => ({
    quickFilter: "",
    lotteries: [],
    regionLotteryParameters: [],
    lottery: {},
    selectedTab: 1,
    loading: true,
    saving: false,
    open1: false,
    open2: false,
    
    // Controle de usuário e permissões
    currentUser: null,
    userResponsibleRegions: [],
    
    // Controle de acordeon - quais loterias estão expandidas
    expandedLotteries: new Set(),
  }),
  computed: {
    // Agrupa as loterias com seus horários e dias configurados
    lotteryGroups() {
      const groups = {};
      
      // Primeiro, cria grupos baseados nas loterias básicas
      this.lotteries.forEach(lottery => {
        groups[lottery.id] = {
          lotteryId: lottery.id,
          lotteryName: lottery.name,
          schedules: []
        };

        // Para cada horário da loteria, adiciona informações dos parâmetros regionais
        lottery.details.forEach(detail => {
          const regionParam = this.regionLotteryParameters.find(
            param => param.lotteryDetailId === detail.id
          );

          // Para ADMIN sem região selecionada, mostrar como ativo por padrão
          const isAdminWithoutRegion = this.isAdmin && this.regionLotteryParameters.length === 0;

          groups[lottery.id].schedules.push({
            lotteryDetailId: detail.id,
            lotteryDetailDescription: detail.description,
            monday: regionParam?.monday || false,
            tuesday: regionParam?.tuesday || false,
            wednesday: regionParam?.wednesday || false,
            thursday: regionParam?.thursday || false,
            friday: regionParam?.friday || false,
            saturday: regionParam?.saturday || false,
            sunday: regionParam?.sunday || false,
            isActive: regionParam?.isActive || isAdminWithoutRegion,
            maxHourToAcceptBet: regionParam?.maxHourToAcceptBet || null,
            maxHourToCancelBet: regionParam?.maxHourToCancelBet || null,
            regionId: regionParam?.regionId || null
          });
        });
      });

      let filteredGroups = Object.values(groups);
      // Filtrar loterias baseado no role do usuário e região selecionada
      const currentRegion = JSON.parse(localStorage.getItem('currentRegion') || 'null');
      
      if (currentRegion) {
        // Se há região selecionada (tanto para ADMIN quanto para outros), filtrar por ela
        filteredGroups = filteredGroups.filter(group => {
          return group.schedules.some(schedule => 
            schedule.regionId === currentRegion.id
          );
        });
        
        // Para cada grupo filtrado, manter apenas os schedules da região atual
        filteredGroups = filteredGroups.map(group => ({
          ...group,
          schedules: group.schedules.filter(schedule => schedule.regionId === currentRegion.id)
        }));
      } else if (this.isAdmin) {
        // Se ADMIN e não há região selecionada, mostrar todas as loterias
        // (comportamento padrão - já temos todos os grupos)
        // Para ADMIN sem região, não aplicar filtro de isActive
      } else {
        // Para usuários não-admin sem região selecionada, não mostrar nada
        filteredGroups = [];
      }
      
      // Filtrar apenas loterias que possuem pelo menos um horário ativo
      // Uma loteria é considerada ativa se pelo menos um de seus horários
      // está configurado como ativo (isActive: true) nos parâmetros regionais
      // Para ADMIN sem região selecionada, pular este filtro
      if (currentRegion || !this.isAdmin) {
        filteredGroups = filteredGroups.filter(group => {
          return group.schedules.some(schedule => schedule.isActive);
        });
      }
      
      return filteredGroups;
    },

    filteredLotteryGroups() {
      if (!this.quickFilter) return this.lotteryGroups;
      return this.lotteryGroups.filter(group => 
        group.lotteryName.toUpperCase().includes(this.quickFilter.toUpperCase())
      );
    },

    isAdmin() {
      return this.currentUser && this.currentUser.role === ROLES.ADMIN;
    },

    isBanca() {
      return this.currentUser && this.currentUser.role === ROLES.BANCA;
    },

    isGerente() {
      return this.currentUser && this.currentUser.role === ROLES.GERENTE;
    },

    canCreateEditLotteries() {
      // ADMIN: acesso total
      // BANCA: pode editar apenas horários (não criar/editar nome/excluir)
      // GERENTE: não pode criar/editar/excluir
      return this.isAdmin;
    },

    canEditSchedules() {
      // ADMIN: pode editar tudo
      // BANCA: pode editar apenas horários
      // GERENTE: não pode editar
      return this.isAdmin || this.isBanca;
    },

    canDeleteLotteries() {
      // ADMIN: pode excluir
      // BANCA: não pode excluir
      // GERENTE: não pode excluir
      return this.isAdmin;
    },

    currentRegion() {
      return JSON.parse(localStorage.getItem('currentRegion') || 'null');
    }
  },
  async mounted() {
    this.loading = true;
    try {
      await this.loadCurrentUser();
      await this.loadLotteriesData();
    } catch (error) {
      this.loading = false;
      toast.error(handleErrorMessage(error));
    }
    
    // Listener para mudanças na região selecionada
    window.addEventListener('storage', (e) => {
      if (e.key === 'currentRegion') {
        this.loadLotteriesData();
      }
    });
  },
  
  beforeUnmount() {
    // Remover listener ao destruir o componente
    window.removeEventListener('storage', this.handleStorageChange);
  },
  methods: {
    async loadCurrentUser() {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          this.currentUser = JSON.parse(userData);
          
          // A região atual é acessada via computed property
          
          // Se não for ADMIN, carregar regiões do usuário responsável  
          if (!this.isAdmin) {
            await this.loadUserResponsibleRegions();
          }
        }
      } catch (error) {
        console.error('Erro ao carregar dados do usuário:', error);
        this.currentUser = null;
        toast.error("Erro ao carregar dados do usuário");
      }
    },

    async loadUserResponsibleRegions() {
      try {
        if (!this.currentUser) return;
        
        const response = await mainService.getUserRegions(this.currentUser.id);
        this.userResponsibleRegions = response.data.map(region => region.id);
        
        // Incluir a região principal também, se existir
        if (this.currentUser.regionId && !this.userResponsibleRegions.includes(this.currentUser.regionId)) {
          this.userResponsibleRegions.push(this.currentUser.regionId);
        }
      } catch (error) {
        console.error('Erro ao carregar regiões do usuário:', error);
        this.userResponsibleRegions = [];
        toast.error("Erro ao carregar regiões do usuário");
      }
    },

    async loadLotteriesData() {
      this.loading = true;
      try {
        // Carrega as loterias básicas
        await this.searchLotteries();
        
        // Carrega os parâmetros regionais das loterias apenas se há região selecionada
        const currentRegion = JSON.parse(localStorage.getItem('currentRegion') || 'null');
        if (currentRegion && currentRegion.id) {
          await this.searchRegionLotteryParameters();
        } else {
          // Se não há região selecionada, limpar parâmetros regionais
          this.regionLotteryParameters = [];
        }
      } catch (error) {
        toast.error("Erro ao carregar dados das loterias");
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    selectTab(tab) {
      if (tab === 2 && !this.canCreateEditLotteries && !this.canEditSchedules) {
        toast.error("Você não tem permissão para criar/editar loterias!");
        return;
      }
      
      this.selectedTab = tab;
      if (tab == 2 && !this.lottery.id) {
        this.createLottery();
      }
    },
    createLottery() {
      if (!this.canCreateEditLotteries) {
        toast.error("Você não tem permissão para criar loterias!");
        return;
      }

      this.lottery = {
        id: null,
        name: null,
        details: [{ id: null, description: null }],
      };
      this.selectedTab = 2;
    },
    editLottery(id) {
      if (!this.canEditSchedules) {
        toast.error("Você não tem permissão para editar loterias!");
        return;
      }

      this.selectedTab = 2;
      this.getById(id);
    },
    reset() {
      this.lottery = {};
      this.selectedTab = 1;
    },
    validateLottery() {
      if (this.isAdmin) {
        // ADMIN pode validar nome e horários
        if (this.lottery.name == null || this.lottery.name == "") {
          toast.error("Informe o nome da loteria!");
          return false;
        } else if (this.lottery.details[0].description == null) {
          toast.error("Informe ao menos um horário!");
          return false;
        } else if (this.lottery.details.filter((c) => c.description == null || c.description == "").length > 0) {
          toast.error("Existem horários adicionados não preenchidos!");
          return false;
        }
      } else if (this.isBanca) {
        // BANCA pode validar apenas horários
        if (this.lottery.details[0].description == null) {
          toast.error("Informe ao menos um horário!");
          return false;
        } else if (this.lottery.details.filter((c) => c.description == null || c.description == "").length > 0) {
          toast.error("Existem horários adicionados não preenchidos!");
          return false;
        }
      } else {
        toast.error("Você não tem permissão para salvar alterações!");
        return false;
      }

      return true;
    },
    searchLotteries() {
      return mainService
        .searchLotteries()
        .then((response) => {
          this.lotteries = response.data;
        })
        .catch((error) => {
          toast.error(handleErrorMessage(error));
          throw error;
        });
    },

    searchRegionLotteryParameters() {
      return mainService
        .searchRegionConfigs()
        .then((response) => {
          this.regionLotteryParameters = response.data.regionLotteriesParameter || [];
        })
        .catch((error) => {
          console.error("Erro ao carregar parâmetros regionais:", error);
          if (error.message === "Nenhuma região selecionada") {
            toast.error("Selecione uma região para visualizar as loterias");
          } else {
            toast.error("Erro ao carregar configurações da região");
          }
          this.regionLotteryParameters = [];
        });
    },

    getById(id) {
      this.loading = true;
      mainService
        .getById(id)
        .then((response) => {
          this.lottery = response.data;
          this.lottery.details.forEach((d) => {
            d.operation = "Update";
          });
          this.lottery.details = this.lottery.details.sort(function (a, b) {
            return a.description > b.description ? 1 : -1;
          });
        })
        .catch((error) => {
          toast.error(handleErrorMessage(error));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async insertLottery() {
      if (this.saving) return;
      this.saving = true;

      try {
        if (!this.canCreateEditLotteries) {
          toast.error("Você não tem permissão para criar loterias!");
          return;
        }

        if (!this.validateLottery()) {
          return;
        }

        await mainService.insertLottery(this.lottery);
        toast.success("Loteria cadastrada com sucesso!");
        this.lottery = {};
        this.loadLotteriesData();
        this.selectedTab = 1;
      } catch (error) {
        toast.error(handleErrorMessage(error));
      } finally {
        this.saving = false;
      }
    },
    async updateLottery() {
      if (this.saving) return;
      this.saving = true;

      try {
        if (!this.canEditSchedules) {
          toast.error("Você não tem permissão para editar loterias!");
          return;
        }

        if (!this.validateLottery()) {
          return;
        }

        await mainService.updateLottery(this.lottery);
        toast.success("Loteria atualizada com sucesso!");
        this.lottery = {};
        this.loadLotteriesData();
        this.selectedTab = 1;
      } catch (error) {
        toast.error(handleErrorMessage(error));
      } finally {
        this.saving = false;
      }
    },
    deleteLottery(idLottery) {
      if (!this.canDeleteLotteries) {
        toast.error("Você não tem permissão para excluir loterias!");
        return;
      }

      this.$confirm({
        message: "Confirma a exclusão?",
        button: { no: "Não", yes: "Sim" },
        callback: (confirm) => {
          if (confirm) {
            mainService
              .deleteLottery(idLottery)
              .then(() => {
                toast.success("Loteria excluída com sucesso!");
                this.loadLotteriesData();
                this.selectedTab = 1;
              })
              .catch((error) => {
                toast.error(handleErrorMessage(error));
              })
              .finally(() => { });
          }
        },
      });
      setTimeout(() => {
        document.getElementsByClassName("vc-btn left")[0].focus();
      }, 1);
    },
    addLotteryTime(idLottery) {
      if (!this.canEditSchedules) {
        toast.error("Você não tem permissão para adicionar horários!");
        return;
      }

      this.lottery.details.push({
        id: null,
        description: null,
        operation: "Add",
      });
    },
    removeLotteryTime(lotteryTime) {
      if (!this.canEditSchedules) {
        toast.error("Você não tem permissão para remover horários!");
        return;
      }

      if (this.lottery.id) {
        lotteryTime.operation = "Delete";
      } else {
        this.lottery.details.splice(this.lottery.details.indexOf(lotteryTime), 1);
      }
    },

    // Retorna os dias ativos para um horário específico
    getActiveDays(schedule) {
      const days = [];
      const dayMap = {
        monday: 'Seg',
        tuesday: 'Ter', 
        wednesday: 'Qua',
        thursday: 'Qui',
        friday: 'Sex',
        saturday: 'Sáb',
        sunday: 'Dom'
      };

      Object.keys(dayMap).forEach(dayKey => {
        if (schedule[dayKey]) {
          days.push(dayMap[dayKey]);
        }
      });

      return days;
    },

    focusTimeInput(event) {
      // Evita que o clique no botão de remover acione este evento
      if (event.target.closest('.btn-remove')) {
        return;
      }
      
      // Encontra o input dentro do grupo clicado
      const input = event.currentTarget.querySelector('.schedule-input');
      if (input) {
        input.focus();
        input.click();
      }
    },
    openTimePicker(event) {
      // Força a abertura do seletor de horário
      const input = event.target;
      
      // Para browsers que suportam showPicker
      if (input.showPicker) {
        try {
          input.showPicker();
        } catch (error) {
          // Fallback se showPicker não funcionar
        }
      }
      
      // Alternativa: simular clique no input
      setTimeout(() => {
        input.focus();
      }, 100);
    },

    // Métodos para controle do acordeon
    toggleLottery(lotteryId) {
      if (this.expandedLotteries.has(lotteryId)) {
        this.expandedLotteries.delete(lotteryId);
      } else {
        this.expandedLotteries.add(lotteryId);
      }
    },

    isLotteryExpanded(lotteryId) {
      return this.expandedLotteries.has(lotteryId);
    },
  },
};
</script>

<style scoped>
/* Mensagem de permissão - seguir padrão das outras páginas */
.permission-info {
  margin-bottom: 1rem;
  padding: 1rem 1.5rem;
  background-color: #3478f2;
  border: 1px solid #548fff;
  border-radius: 8px;
  color: white;
}

.info-card {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-content {
  font-size: 0.9em;
  line-height: 1.4;
}

/* Mensagem de informação para BANCA */
.banca-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 10px;
  background-color: #e9ecef;
  border-radius: 6px;
  font-size: 0.85em;
  color: var(--text-muted, #6c757d);
}

/* Aba desabilitada */
.tab-button.disabled {
  opacity: 0.5;
  cursor: not-allowed !important;
  pointer-events: none;
}

.tab-button.disabled:hover {
  background-color: var(--bg-secondary) !important;
  color: var(--text-muted) !important;
}

/* Botões desabilitados */
.btn-remove:disabled,
.btn-add-schedule:disabled,
.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.btn-action:disabled {
  opacity: 0.3;
  cursor: not-allowed;
  pointer-events: none;
}

/* Container da tabela/listagem com grupos de loterias */
.lottery-datatable {
  border-radius: 8px;
  overflow: hidden;
  padding: 2rem;
}

.lottery-group-row {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  margin-bottom: 1rem;
  overflow: hidden;
  transition: all 0.2s ease;
}

.lottery-group-row:hover {
  box-shadow: var(--shadow-md);
}

.lottery-group-header {
  background: linear-gradient(135deg, var(--secondary-color), var(--card-background));
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;
}

.lottery-group-header:hover {
  background: linear-gradient(135deg, var(--secondary-color), var(--input-background));
}

.lottery-group-header.expanded {
  border-bottom-color: var(--accent-color);
}

.lottery-main-info {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.accordion-icon {
  color: var(--accent-color);
  font-size: 0.875rem;
  transition: transform 0.3s ease;
  flex-shrink: 0;
}

.accordion-icon.rotated {
  transform: rotate(180deg);
}

.lottery-main-title {
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.lottery-main-title i {
  color: var(--accent-color);
}

.lottery-main-actions {
  display: flex;
  gap: 0.5rem;
}

/* Cards dos horários */
.schedule-cards-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 1rem 1.5rem;
  overflow: hidden;
}

/* Animações do acordeon */
.accordion-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.accordion-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.accordion-enter-from {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
  margin-top: 0;
  margin-bottom: 0;
}

.accordion-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
  margin-top: 0;
  margin-bottom: 0;
}

.accordion-enter-to,
.accordion-leave-from {
  max-height: 2000px;
  opacity: 1;
}

.schedule-card {
  background: var(--input-background);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 12px;
  flex: 1 1 calc(25% - 9px);
  min-width: 200px;
  max-width: calc(25% - 9px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: all 0.3s ease;
}

.schedule-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px var(--shadow);
}

.schedule-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.schedule-time {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.time-text {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: var(--danger);
}

.status-active {
  background-color: var(--success);
}

.status-inactive {
  background-color: var(--danger);
}

.schedule-card-days {
  margin-top: 8px;
}

.days-mini-label {
  font-size: 12px;
  color: var(--text-muted);
  margin-bottom: 4px;
  display: block;
}

.days-badges-mini {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.day-badge-mini {
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 500;
  background-color: var(--bg-tertiary);
  color: var(--text-secondary);
}

.day-badge-mini.active {
  background-color: var(--primary-color);
  color: white;
}

.no-days-mini {
  color: var(--danger);
  font-style: italic;
  font-size: 12px;
}

.no-schedules-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: var(--danger);
  font-style: italic;
  text-align: center;
  justify-content: center;
  flex: 1 1 calc(25% - 9px);
  min-width: 200px;
  max-width: calc(25% - 9px);
}

/* Seção de horários no formulário */
.schedules-section {
  margin-top: 2rem;
}

.schedules-header {
  margin-bottom: 1.5rem;
}

.schedules-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
}

.schedules-title i {
  color: var(--accent-color);
}

.schedules-subtitle {
  color: var(--text-secondary);
  font-size: 0.95rem;
  margin: 0;
}

.schedules-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.schedule-item {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.3s ease;
}

.schedule-item:hover {
  box-shadow: 0 2px 8px var(--shadow);
}

.schedule-input-group {
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
}

.schedule-icon {
  color: var(--accent-color);
  font-size: 1.125rem;
  flex-shrink: 0;
}

.schedule-input {
  flex: 1;
  padding: 0.75rem 1rem;
  border: 2px solid var(--border-color);
  border-radius: 6px;
  background: var(--input-background);
  color: var(--text-primary);
  font-size: 0.95rem;
  transition: all 0.3s ease;
  cursor: pointer;
}

.schedule-input:focus {
  outline: none;
  border-color: var(--accent-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.btn-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: var(--danger);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.btn-remove:hover:not(:disabled) {
  background: var(--danger-hover);
  transform: scale(1.05);
}

.add-schedule {
  margin-top: 1rem;
}

.btn-add-schedule {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background: var(--accent-color);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
}

.btn-add-schedule:hover:not(:disabled) {
  background: var(--accent-hover);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px var(--shadow);
}

/* Estado vazio */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
}

.empty-state-content {
  text-align: center;
  color: var(--text-secondary);
}

.empty-state-content i {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.3;
}

.empty-state-content h3 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: var(--text-primary);
}

.empty-state-content p {
  font-size: 1rem;
  margin: 0;
}

.empty-state-note {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: var(--bg-secondary);
  border-radius: 6px;
  color: var(--text-muted);
  font-size: 0.875rem;
}

/* Responsividade */
@media (max-width: 768px) {
  .lottery-group-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .lottery-main-actions {
    align-self: flex-end;
  }

  .schedule-input-group {
    flex-direction: column;
    gap: 0.75rem;
    align-items: stretch;
  }

  .schedule-cards-container {
    gap: 10px;
  }

  .schedule-card {
    flex: 1 1 calc(50% - 5px);
    max-width: calc(50% - 5px);
  }

  .no-schedules-card {
    flex: 1 1 calc(50% - 5px);
    max-width: calc(50% - 5px);
  }
}

@media (max-width: 480px) {
  .schedule-card {
    flex: 1 1 100%;
    max-width: 100%;
  }

  .no-schedules-card {
    flex: 1 1 100%;
    max-width: 100%;
  }

  .lottery-group-header {
    padding: 1rem;
  }

  .schedule-cards-container {
    padding: 1rem;
  }
}
</style>

